import { useMagicKeys, whenever } from "@vueuse/core";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import startCase from "lodash/startCase";

import { ModifierKeys } from "@/enums/keyboardShortcuts";
import { ShortcutContext } from "@/enums/keyboardShortcuts";
import { OPEN_TEMPLATES } from "@/helpers/constants/events";
import { useAuth } from "@/hooks/auth";
import { useEmitter } from "@/hooks/common";

import type { TextEditorShortcut } from "@/enums/keyboardShortcuts";
import type {
  KeyboardShortcutOption,
  UserKeyboardShortcut,
} from "@/models/options";
import type { Quill } from "@vueup/vue-quill";

const isMacOs = computed(() =>
  window.navigator.userAgent.toLowerCase().includes("mac")
);
const metaKey = computed(() => (isMacOs.value ? "⌘" : "CTRL"));

export const useKeyboardShortcuts = () => {
  const auth = useAuth();
  const { getters } = useStore();
  const { t } = useI18n();
  const emitter = useEmitter();

  const userShortcuts = computed<Record<string, KeyboardShortcutOption>>(
    () => getters["options/userKeyboardShortcuts"]
  );

  const configuredShortcuts = computed<UserKeyboardShortcut[]>(() => {
    let shortcuts = Object.entries(userShortcuts.value).map(([key, value]) => ({
      ...value,
      key,
    }));
    if (!isMacOs.value) {
      shortcuts = convertToWindows(shortcuts);
    }
    return attachMagicKeys(shortcuts);
  });

  const groupedShortcuts = computed(() => [
    {
      context: t("SHORTCUTS.GLOBAL"),
      shortcuts: configuredShortcuts.value.filter(
        (sc) => sc.context === ShortcutContext.Global
      ),
    },
    {
      context: t("SHORTCUTS.DEALS_LIST"),
      shortcuts: configuredShortcuts.value.filter(
        (sc) => sc.context === ShortcutContext.DealsList
      ),
    },
    {
      context: t("SHORTCUTS.ACTIVE_DEAL"),
      shortcuts: configuredShortcuts.value.filter(
        (sc) => sc.context === ShortcutContext.ActiveDeal
      ),
    },
    {
      context: t("SHORTCUTS.CALL_DISPOSITION"),
      shortcuts: configuredShortcuts.value.filter(
        (sc) => sc.context === ShortcutContext.CallDisposition
      ),
    },
    {
      context: t("SHORTCUTS.EMAIL_SMS"),
      shortcuts: configuredShortcuts.value.filter(
        (sc) => sc.context === ShortcutContext.EmailSms
      ),
    },
  ]);

  type ConversionMap = {
    action?: string;
    action_string?: string;
    modifiers?: ModifierKeys[];
    keys?: string[];
  };

  const WINDOWS_CONVERSION_MAP: Record<string, ConversionMap> = {
    // required only for default shortcuts as customized shortcuts will send the correct values
    cmd_shift_d: {
      action: "CTRL + ALT + D",
      action_string: "ctrl_alt_d",
      modifiers: [ModifierKeys.Ctrl, ModifierKeys.Alt],
    },
    cmd_shift_a: {
      action: "CTRL + ALT + A",
      action_string: "ctrl_alt_a",
      modifiers: [ModifierKeys.Ctrl, ModifierKeys.Alt],
    },
    cmd_shift_i: {
      action: "CTRL + ALT + I",
      action_string: "ctrl_alt_i",
      modifiers: [ModifierKeys.Ctrl, ModifierKeys.Alt],
    },
    cmd_backslash: {
      action: "CTRL + SHIFT + \\",
      action_string: "ctrl_shift_backslash",
      modifiers: [ModifierKeys.Ctrl, ModifierKeys.Shift],
    },
    alt_left: {
      action: "ALT + UP",
      action_string: "alt_up",
    },
    alt_right: {
      action: "ALT + DOWN",
      action_string: "alt_down",
    },
    alt_ß: {
      action_string: "alt_s",
      keys: ["s"],
    },
    alt_ƒ: {
      action_string: "alt_f",
      keys: ["f"],
    },
    "alt_˚": {
      action_string: "alt_k",
      keys: ["k"],
    },
    "alt_∆": {
      action_string: "alt_j",
      keys: ["j"],
    },
    alt_ç: {
      action_string: "alt_c",
      keys: ["c"],
    },
    "alt_shift_´": {
      action_string: "alt_shift_e",
      keys: ["e"],
    },
    "alt_†": {
      action_string: "alt_t",
      keys: ["t"],
    },
    "alt_≤": {
      action_string: "alt_,",
      keys: [","],
    },
    "alt_≥": {
      action_string: "alt_.",
      keys: ["."],
    },
    "alt_∂": {
      action_string: "alt_d",
      keys: ["d"],
    },
    alt_π: {
      action_string: "alt_p",
      keys: ["p"],
    },
    alt_å: {
      action_string: "alt_a",
      keys: ["a"],
    },
    "alt_¬": {
      action_string: "alt_l",
      keys: ["l"],
    },
    "alt_®": {
      action_string: "alt_r",
      keys: ["r"],
    },
    "alt_shift_˜": {
      action_string: "alt_shift_n",
      keys: ["n"],
    },
    "alt_shift_¨": {
      action_string: "alt_shift_u",
      keys: ["u"],
    },
    alt_shift_slash: {
      keys: ["?"],
    },
    alt_ω: {
      action_string: "alt_z",
      keys: ["z"],
    },
    alt_shift_Â: {
      action_string: "alt_shift_m",
      keys: ["m"],
    },
    alt_shift_Î: {
      action_string: "alt_shift_d",
      keys: ["d"],
    },
    "alt_¡": {
      action_string: "alt_1",
      keys: ["1"],
    },
    "alt_™": {
      action_string: "alt_2",
      keys: ["2"],
    },
    "alt_£": {
      action_string: "alt_3",
      keys: ["3"],
    },
    "alt_¢": {
      action_string: "alt_4",
      keys: ["4"],
    },
    "alt_∞": {
      action_string: "alt_5",
      keys: ["5"],
    },
    "alt_§": {
      action_string: "alt_6",
      keys: ["6"],
    },
    "alt_¶": {
      action_string: "alt_7",
      keys: ["7"],
    },
    cmd_shift_n: {
      action: "CTRL + SHIFT + N",
      action_string: "ctrl_shift_n",
      modifiers: [ModifierKeys.Ctrl, ModifierKeys.Shift],
    },
    cmd_shift_e: {
      action: "CTRL + E",
      action_string: "ctrl_e",
      modifiers: [ModifierKeys.Ctrl],
    },
  };

  const formatActionName = (actionName: string) => {
    return startCase(actionName);
  };

  const convertToWindows = (shortcuts: UserKeyboardShortcut[]) => {
    return shortcuts.map((sc) => {
      const conversion =
        WINDOWS_CONVERSION_MAP[
          sc.action_string as keyof typeof WINDOWS_CONVERSION_MAP
        ];

      const {
        action: newFriendlyAction,
        action_string: newActionString,
        modifiers: newModifiers,
        keys: newKeys,
      } = conversion ?? {};
      const {
        action: oldFriendlyAction,
        action_string: oldActionString,
        modifiers: oldModifiers,
        keys: oldKeys,
      } = sc;

      const friendlyAction = (newFriendlyAction ?? oldFriendlyAction)
        .replace("⌘", "CTRL")
        .replace("OPTION", "ALT");
      const actionString = (newActionString ?? oldActionString).replace(
        "cmd",
        "ctrl"
      );
      const modifiers =
        (newModifiers ?? oldModifiers ?? []).map((modifier) =>
          modifier === ModifierKeys.Meta ? ModifierKeys.Ctrl : modifier
        ) ?? [];
      const keys = newKeys ?? oldKeys;

      return {
        ...sc,
        friendlyAction: newFriendlyAction ?? friendlyAction,
        actionString: newActionString ?? actionString,
        modifiers,
        keys,
      };
    });
  };

  const attachMagicKeys = (shortcuts: UserKeyboardShortcut[]) => {
    return shortcuts.map((sc) => {
      if (sc.keys.length > 1) {
        // more than one shortcut key is a sequence eg. "g then n"
        // not a key combination
        const { success } = useMagicSequence(sc);
        return { ...sc, shortcutTriggered: success };
      }
      const { [sc.action_string]: magicKeys } = useMagicKeys({
        passive: false,
        onEventFired(e) {
          const key = sc.keys[0];
          const allModifiersPressed = sc.modifiers?.every(
            (modifier) => e[modifier]
          );
          if (allModifiersPressed && e.key === key) {
            e.preventDefault();
          }
        },
      });
      return { ...sc, shortcutTriggered: magicKeys };
    });
  };

  const getShortcutForAction = (actionName: string) => {
    return configuredShortcuts.value.find(
      (sc) => sc.action_name === actionName
    );
  };

  const assignShortcutToAction = (
    actionName: string,
    action: () => void,
    canPerformAction = () => true
  ) => {
    // Re-bind to the latest Ref whenever the user shortcuts change.
    const triggerRef = computed(
      () => getShortcutForAction(actionName)?.shortcutTriggered
    );
    watch(
      triggerRef,
      (newTrigger, _oldTrigger, onCleanup) => {
        if (!newTrigger) return;
        const stopWhenever = whenever(newTrigger, () => {
          if (!canPerformAction()) return;
          if (auth.currentUser) {
            action();
          }
        });
        onCleanup(() => {
          stopWhenever();
        });
      },
      { immediate: true }
    );
  };

  const useMagicSequence = (shortcut: UserKeyboardShortcut) => {
    const magicKeys = useMagicKeys({});

    const i = ref(0);
    const down = ref(false);
    const success = ref(false);

    watch(
      () => magicKeys.current,
      () => {
        if (magicKeys[shortcut.keys[i.value]].value && !down.value) {
          down.value = true;
          i.value += 1;
        } else if (
          i.value > 0 &&
          !magicKeys[shortcut.keys[i.value - 1]].value &&
          down.value
        ) {
          down.value = false;
        } else {
          i.value = 0;
          down.value = false;
        }
        if (i.value >= shortcut.keys.length && !down.value) {
          i.value = 0;
          down.value = false;
          success.value = true;
          setTimeout(() => (success.value = false), 10);
        }
      },
      {
        deep: true,
      }
    );
    return {
      success,
    };
  };

  type BindingDescriptor = {
    key: string;
    [ModifierKeys.Alt]?: boolean;
    [ModifierKeys.Ctrl]?: boolean;
    [ModifierKeys.Meta]?: boolean;
    [ModifierKeys.Shift]?: boolean;
  };

  const buildBindingDescriptor = (shortcut: UserKeyboardShortcut) => {
    const desc: BindingDescriptor = { key: shortcut.keys[0].toUpperCase() };
    (shortcut.modifiers ?? []).forEach((modifier) => {
      if (
        [
          ModifierKeys.Alt,
          ModifierKeys.Ctrl,
          ModifierKeys.Meta,
          ModifierKeys.Shift,
        ].includes(modifier)
      ) {
        desc[modifier] = true;
      }
    });
    return desc;
  };
  /* eslint-disable @typescript-eslint/no-explicit-any */

  const handleQuillShortcut = (
    quill: Quill,
    actionName: TextEditorShortcut,
    range: any,
    context: any
  ): boolean => {
    if (!range) {
      return false;
    }

    switch (actionName) {
      case "bold":
      case "italic":
      case "underline":
      case "strike": {
        const isOn = !!context.format[actionName];
        quill.format(actionName, !isOn);
        return false;
      }

      case "orderedList": {
        const isOn = context.format.list === "ordered";
        quill.format("list", isOn ? false : "ordered");
        return false;
      }

      case "unorderedList": {
        const isOn = context.format.list === "bullet";
        quill.format("list", isOn ? false : "bullet");
        return false;
      }

      case "link": {
        const toolbar = quill.getModule("toolbar");
        if (toolbar?.handlers?.link) {
          const isLink = !!context.format.link;
          toolbar.handlers.link.call(toolbar, !isLink);
        } else {
          // fallback
          const prevValue = context.format.link || "";
          const value = window.prompt("Enter link", prevValue || "https://");
          if (value) {
            quill.format("link", value);
          } else if (prevValue) {
            quill.format("link", false);
          }
        }
        return false;
      }

      case "chooseTemplate": {
        emitter.emit(OPEN_TEMPLATES);
        return false;
      }

      default: {
        return false;
      }
    }
  };

  const registerQuillShortcuts = (
    quill: Quill,
    shortcuts: UserKeyboardShortcut[]
  ) => {
    const kb = quill.getModule("keyboard");

    shortcuts.forEach((shortcut) => {
      const desc = buildBindingDescriptor(shortcut);
      kb.addBinding(desc, (range: any, context: any) => {
        return handleQuillShortcut(
          quill,
          shortcut.action_name as TextEditorShortcut,
          range,
          context
        );
      });
    });
  };
  /* eslint-enable @typescript-eslint/no-explicit-any */

  return {
    metaKey,
    getShortcutForAction,
    assignShortcutToAction,
    isMacOs,
    userShortcuts,
    configuredShortcuts,
    groupedShortcuts,
    formatActionName,
    registerQuillShortcuts,
  };
};

export const useCustomizeKeyboardShortcuts = () => {
  const macOsShortcuts = new Set([
    "⌘ + Space",
    "⌘ + Tab",
    "⌘ + Q",
    "⌘ + W",
    "⌘ + H",
    "⌘ + M",
    "⌘ + Shift + 3",
    "⌘ + Shift + 4",
    "⌘ + Shift + 5",
    "CTRL + ARROWUP",
    "CTRL + ARROWDOWN",
    "⌘ + CTRL + Q",
  ]);

  const winOsShortcuts = new Set([
    "META + D",
    "META + E",
    "META + L",
    "META + R",
    "META + Tab",
    "ALT + Tab",
    "ALT + F4",
    "CTRL + ALT + Delete",
    "META + PrintScreen",
  ]);

  const macBrowserShortcuts = new Set([
    "⌘ + T",
    "⌘ + N",
    "SHIFT + ⌘ + N",
    "⌘ + R",
    "⌘ + J",
    "SHIFT + ⌘ + R",
    "⌘ + F",
    "⌘ + P",
    "⌘ + S",
    "⌘ + O",
    "⌘ + Y",
    "SHIFT + ⌘ + J",
    "SHIFT + ⌘ + Delete",
    "⌘ + Plus",
    "⌘ + Minus",
    "⌘ + 0",
  ]);

  const winBrowserShortcuts = new Set([
    "CTRL + T",
    "CTRL + W",
    "CTRL + N",
    "CTRL + SHIFT + N",
    "CTRL + Tab",
    "CTRL + SHIFT + Tab",
    "CTRL + R",
    "CTRL + SHIFT + R",
    "CTRL + F",
    "CTRL + P",
    "CTRL + S",
    "CTRL + O",
    "CTRL + H",
    "CTRL + J",
    "CTRL + SHIFT + Delete",
    "CTRL + Plus",
    "CTRL + Minus",
    "CTRL + 0",
    "F5",
    "F11",
    "F12",
  ]);

  const conflicts = isMacOs.value
    ? new Set([...macOsShortcuts, ...macBrowserShortcuts])
    : new Set([...winOsShortcuts, ...winBrowserShortcuts]);

  const macOptionCharacterReplacementMap = {
    å: "A",
    "∫": "B",
    ç: "C",
    "∂": "D",
    "´": "E", // Dead key
    ƒ: "F",
    "©": "G",
    "˙": "H",
    ˆ: "I", // Dead key
    "∆": "J",
    "˚": "K",
    "¬": "L",
    µ: "M",
    "˜": "N", // Dead key
    ø: "O",
    π: "P",
    œ: "Q",
    "®": "R",
    ß: "S",
    "†": "T",
    "¨": "U", // Dead key
    "√": "V",
    "∑": "W",
    "≈": "X",
    "¥": "Y",
    Ω: "Z",
    "¡": "1",
    "™": "2",
    "£": "3",
    "¢": "4",
    "∞": "5",
    "§": "6",
    "¶": "7",
    "•": "8",
    ª: "9",
    º: "0",
    "–": "-",
    "≠": "=",
    "≤": ",",
    "≥": ".",
    "÷": "/",
    '"': "[",
    "'": "]",
    "«": "\\",
    æ: "'",
    "…": ";",
    Î: "D",
    ω: "Z",
    Â: "M",
  };

  const checkForConflict = (shortcut: string) => conflicts.has(shortcut);

  const captureKeyboardShortcut = (evt: KeyboardEvent) => {
    const DEAD_KEY = "Dead";
    const modifiers: string[] = [];
    const magicKeysModifiers: string[] = [];

    if (evt.ctrlKey) {
      modifiers.push("CTRL");
      magicKeysModifiers.push("ctrl");
    }
    if (evt.altKey) {
      modifiers.push(isMacOs.value ? "OPTION" : "ALT");
      magicKeysModifiers.push("alt");
    }
    if (evt.shiftKey) {
      modifiers.push("SHIFT");
      magicKeysModifiers.push("shift");
    }
    if (evt.metaKey) {
      modifiers.push(isMacOs.value ? "⌘" : "META");
      magicKeysModifiers.push(isMacOs.value ? "cmd" : "ctrl");
    }

    const key = evt.key;

    const shortcutParts = [...modifiers];
    let normalizedKey = key.toUpperCase();
    if (key === " ") {
      normalizedKey = "Space";
    }
    if (key === DEAD_KEY && evt.code.indexOf("Key") > -1) {
      normalizedKey = evt.code.split("Key")[1];
    }

    // replace `OPTION + *` mapped key combo with keyboard key equivalent for display to users
    if (
      isMacOs.value &&
      evt.altKey &&
      macOptionCharacterReplacementMap[
        key as keyof typeof macOptionCharacterReplacementMap
      ]
    ) {
      normalizedKey =
        macOptionCharacterReplacementMap[
          key as keyof typeof macOptionCharacterReplacementMap
        ];
    }

    shortcutParts.push(normalizedKey);
    const shortcut = shortcutParts.join(" + ");

    if (key === DEAD_KEY) {
      // we cannot recognize a 'dead' keypress in mac os
      return {
        hasError: true,
        action: shortcut,
      };
    }

    if (
      ["Control", "Alt", "Shift", "Meta", "Command", "Option"].includes(key)
    ) {
      return {
        pending: true,
        modifiers,
      };
    }

    // Build the VueUse MagicKeys format (with _)
    const magicKeysParts = [...magicKeysModifiers];

    // Normalize key for MagicKeys API (lowercase, special handling for arrows)
    let magicKeysKey = key.toLowerCase();
    if (key === " ") {
      magicKeysKey = "space";
    } else if (key.startsWith("Arrow")) {
      magicKeysKey = key.replace("Arrow", "").toLowerCase();
    } else if (key === "\\") {
      magicKeysKey = "backslash";
    } else if (key === "/") {
      magicKeysKey = "slash";
    } else if (key.length === 1) {
      magicKeysKey = key.toLowerCase();
    }

    magicKeysParts.push(magicKeysKey);
    const magicKeysShortcut = magicKeysParts.join("_");

    const hasConflict = checkForConflict(shortcut);

    return {
      hasConflict: hasConflict,
      isValid: modifiers.length > 0,
      action: shortcut,
      action_string: magicKeysShortcut,
      keys: [key],
      modifiers,
    };
  };

  const mapModifiers = (modifiers: string[]): ModifierKeys[] => {
    return modifiers
      .map((modifier) => {
        if (modifier === "CTRL") {
          return ModifierKeys.Ctrl;
        }
        if (modifier === "ALT" || modifier === "OPTION") {
          return ModifierKeys.Alt;
        }
        if (modifier === "SHIFT") {
          return ModifierKeys.Shift;
        }
        if (modifier === "⌘" || modifier === "META") {
          return ModifierKeys.Meta;
        }
        return undefined;
      })
      .filter(Boolean) as ModifierKeys[];
  };

  const mapConfiguredShortcuts = (shortcuts: UserKeyboardShortcut[]) => {
    return shortcuts.reduce((prev, curr) => {
      const {
        action,
        action_name,
        action_string,
        context,
        is_custom,
        key,
        keys,
        modifiers,
      } = curr;
      return {
        ...prev,
        [key]: {
          action,
          action_name,
          action_string,
          context,
          is_custom,
          keys,
          modifiers,
        },
      };
    }, {});
  };

  return {
    checkForConflict,
    captureKeyboardShortcut,
    mapModifiers,
    mapConfiguredShortcuts,
  };
};
